import { useContext, useEffect, useState } from "react";
import { subtractFloats, toFixed } from "@shared/utils/numbers";
import { sortByNumberProperty } from "@shared/utils/general";
import { OrderTypes } from "@shared/features/trades/common/types";
import { OrderCardData, OrderStatus } from "@shared/types";
import {
  EditOrderData,
  TradeAmountData,
  UpdateOrder,
} from "@shared/features/trades/EditTrade/types";
import {
  getEntryEditOrdersWithUpdatedRatios,
  getOrdersDefaultSortOrder,
  getTradeAmount,
  getWeightedAverageEditOrderPrice,
  redistributeOrdersWithAddedRatio,
  updateEditOrdersWithAddedAmount,
  updateEntryOrdersWithAddedQuantity,
} from "@shared/features/trades/common/utils";
import { TradeDetailsContext } from "@shared/features/trades/TradeDetails.context";

interface UseEditTradeProps {
  amountData: TradeAmountData;
  quantityData: TradeAmountData;
  onValuesModified: () => void;
}

const useEditTradeOrders = ({
  amountData,
  quantityData,
  onValuesModified,
}: UseEditTradeProps) => {
  const { ordersData } = useContext(TradeDetailsContext)!;
  const [entryOrders, setEntryOrders] = useState<EditOrderData[]>([]);
  const [takeProfitOrders, setTakeProfitOrders] = useState<EditOrderData[]>([]);
  const [stopOrders, setStopOrders] = useState<EditOrderData[]>([]);

  const currentPrice = ordersData?.data.current_price;
  const precisionConfig = ordersData?.precision;
  const isShort = !!ordersData?.data.reverse;
  const isBreakout = !!ordersData?.data.breakout;
  const breakevenPrice = toFixed(
    getWeightedAverageEditOrderPrice(entryOrders),
    precisionConfig?.price || 0
  );

  const convertOrdersCardsToEditOrders = (
    ordersCards: OrderCardData[],
    orderType: OrderTypes
  ): EditOrderData[] => {
    const sortOrder = getOrdersDefaultSortOrder(orderType, isShort, isBreakout);

    return ordersCards
      .map((orderCard) => {
        return {
          ...orderCard,
          edit_target_price: orderCard.target_price,
          edit_amount: orderCard.amount,
          edit_ratio: orderCard.ratio,
          edit_quantity: orderCard.quantity,
        };
      })
      .sort((a, b) => sortByNumberProperty(a, b, "target_price", sortOrder));
  };

  const convertEntryOrderCards = (
    entryCards: OrderCardData[]
  ): EditOrderData[] => {
    const existingQuantityOrders = entryCards.filter(
      (orderCard) => orderCard.reason.toLowerCase() === "existing quantity"
    );
    const regularOrders = entryCards.filter(
      (orderCard) => orderCard.reason.toLowerCase() !== "existing quantity"
    );
    return [
      ...convertOrdersCardsToEditOrders(
        existingQuantityOrders,
        OrderTypes.ENTRY
      ),
      ...convertOrdersCardsToEditOrders(regularOrders, OrderTypes.ENTRY),
    ];
  };

  const resetOrdersChanges = () => {
    if (ordersData) {
      setEntryOrders(
        convertEntryOrderCards(ordersData?.data.entry_order_cards)
      );
      setTakeProfitOrders(
        convertOrdersCardsToEditOrders(
          ordersData?.data.take_profit_order_cards,
          OrderTypes.TAKE_PROFIT
        )
      );
      setStopOrders(
        convertOrdersCardsToEditOrders(
          ordersData?.data.stop_order_cards,
          OrderTypes.STOP
        )
      );
    }
  };

  useEffect(() => {
    resetOrdersChanges();
  }, [ordersData]);

  const updateOrder: UpdateOrder = (
    orderType: OrderTypes,
    orderIndex: number,
    updatedOrder: EditOrderData
  ) => {
    if (orderType === OrderTypes.ENTRY) {
      const updatedOrders = [...entryOrders];
      updatedOrders[orderIndex] = updatedOrder;
      const updatedTradeAmount = getTradeAmount(updatedOrders);
      amountData.set(updatedTradeAmount);
      setEntryOrders(
        getEntryEditOrdersWithUpdatedRatios(
          updatedOrders,
          precisionConfig?.percent || 4,
          updatedTradeAmount
        )
      );
    } else if (orderType === OrderTypes.TAKE_PROFIT) {
      const updatedOrders = [...takeProfitOrders];
      updatedOrders[orderIndex] = updatedOrder;
      setTakeProfitOrders(updatedOrders);
    } else if (orderType === OrderTypes.STOP) {
      const updatedOrders = [...stopOrders];
      updatedOrders[orderIndex] = updatedOrder;
      setStopOrders(updatedOrders);
    }
    onValuesModified();
  };

  const recalculateTradeAmountAndNormalize = (entryOrder: EditOrderData[]) => {
    const updatedTradeAmount = getTradeAmount(entryOrder);
    amountData.set(updatedTradeAmount);
    return getEntryEditOrdersWithUpdatedRatios(
      entryOrder,
      precisionConfig?.percent || 4,
      updatedTradeAmount
    );
  };

  const getCancelledOrder = (order: EditOrderData) => {
    return {
      ...order,
      edit_amount: order.filled_amount,
      edit_ratio: order.ratio_filled * order.ratio,
      status: order.filled_amount
        ? OrderStatus.PARTIALLY_FULFILLED
        : OrderStatus.CANCELLED,
      edit_quantity: order.filled_quantity,
      reason: "Manually Cancelled",
    };
  };

  const cancelOrder = (
    orderType: OrderTypes,
    orderIndex: number,
    redistribute: boolean = false
  ) => {
    let updatedOrders = [...getOrdersByType(orderType)];
    let order: EditOrderData | undefined = updatedOrders[orderIndex];
    const removedAmount = subtractFloats(
      order.edit_amount,
      order.filled_amount
    );
    const removedRatio = subtractFloats(
      order.edit_ratio,
      order.ratio_filled * order.edit_ratio
    );
    if (order.id) {
      updatedOrders[orderIndex] = getCancelledOrder(order);
    } else {
      updatedOrders.splice(orderIndex, 1);
    }
    if (redistribute) {
      if (orderType === OrderTypes.TAKE_PROFIT) {
        updatedOrders = redistributeOrdersWithAddedRatio(
          updatedOrders,
          removedRatio,
          order.id ? orderIndex : undefined
        );
      } else {
        updatedOrders = updateEditOrdersWithAddedAmount(
          updatedOrders,
          removedAmount,
          amountData.precision,
          quantityData.precision,
          isShort,
          isBreakout
        );
      }
    }
    if (orderType === OrderTypes.ENTRY) {
      updatedOrders = recalculateTradeAmountAndNormalize(updatedOrders);
    }
    setOrdersByType(orderType, updatedOrders);
  };

  const addOrder = (orderType: OrderTypes, order: EditOrderData) => {
    let updatedOrders = [...getOrdersByType(orderType), order];
    if (orderType === OrderTypes.ENTRY) {
      const updatedTradeAmount = getTradeAmount(updatedOrders);
      updatedOrders = getEntryEditOrdersWithUpdatedRatios(
        updatedOrders,
        precisionConfig?.percent || 4,
        updatedTradeAmount
      );
      amountData.set(updatedTradeAmount);
    }
    setOrdersByType(orderType, updatedOrders);
    return updatedOrders;
  };

  const getOrdersByType = (orderType: OrderTypes): EditOrderData[] => {
    if (orderType === OrderTypes.ENTRY) {
      return entryOrders;
    } else if (orderType === OrderTypes.TAKE_PROFIT) {
      return takeProfitOrders;
    } else {
      return stopOrders;
    }
  };

  const setOrdersByType = (orderType: OrderTypes, orders: EditOrderData[]) => {
    if (orderType === OrderTypes.ENTRY) {
      setEntryOrders(orders);
    } else if (orderType === OrderTypes.TAKE_PROFIT) {
      setTakeProfitOrders(orders);
    } else {
      setStopOrders(orders);
    }
    onValuesModified();
  };

  const updateOrderPrice = (
    orderType: OrderTypes,
    orderIndex: number,
    price: number
  ) => {
    const updatedOrders = [...getOrdersByType(orderType)];
    const order = updatedOrders[orderIndex];
    updatedOrders[orderIndex] = { ...order, edit_target_price: price! };
    setOrdersByType(orderType, updatedOrders);
    onValuesModified();
  };

  const moveOrderToCurrentPrice = (orderType: OrderTypes, orderIndex: number) =>
    updateOrderPrice(orderType, orderIndex, currentPrice!);

  const moveStopOrderToBreakevenPrice = (
    entryOrderToCancelIndices: number[]
  ) => {
    updateOrderPrice(OrderTypes.STOP, 0, breakevenPrice);
    if (entryOrderToCancelIndices.length) {
      let updatedEntryOrders: EditOrderData[] = [];
      entryOrders.forEach((order, orderIndex) => {
        if (entryOrderToCancelIndices.includes(orderIndex)) {
          if (order.id) {
            updatedEntryOrders.push(getCancelledOrder(order));
          }
        } else {
          updatedEntryOrders.push(order);
        }
      });
      updatedEntryOrders =
        recalculateTradeAmountAndNormalize(updatedEntryOrders);
      setOrdersByType(OrderTypes.ENTRY, updatedEntryOrders);
    }
  };

  const updateOrdersWithAddedAmount = (addedAmount: number) => {
    const entryOrdersWithAddedAmount = updateEditOrdersWithAddedAmount(
      entryOrders,
      addedAmount,
      amountData.precision,
      quantityData.precision,
      isShort,
      isBreakout
    );
    const tradeAmount = getTradeAmount(entryOrdersWithAddedAmount);
    amountData.set(tradeAmount);
    const entryOrderWithUpdatedRatios = getEntryEditOrdersWithUpdatedRatios(
      entryOrdersWithAddedAmount,
      precisionConfig?.percent || 4,
      tradeAmount
    );
    setEntryOrders(entryOrderWithUpdatedRatios);

    onValuesModified();
  };

  const updateOrdersWithAddedQuantity = (addedQuantity: number) => {
    const updatedTradeQuantity = quantityData.selected + addedQuantity;
    quantityData.set(updatedTradeQuantity);

    const entryOrdersWithAddedAmount = updateEntryOrdersWithAddedQuantity(
      entryOrders,
      addedQuantity,
      amountData.precision,
      quantityData.precision,
      currentPrice || 0,
      ordersData?.data.leverage || 1,
      quantityData?.conversionFormula
    );
    const updatedTradeAmount = getTradeAmount(entryOrdersWithAddedAmount);
    amountData.set(updatedTradeAmount);

    const entryOrderWithUpdatedRatios = getEntryEditOrdersWithUpdatedRatios(
      entryOrdersWithAddedAmount,
      precisionConfig?.percent || 4,
      updatedTradeAmount
    );
    setEntryOrders(entryOrderWithUpdatedRatios);
    onValuesModified();
  };

  let activeEntryOrders: EditOrderData[] | undefined;
  const numActiveEntries = ordersData?.data.number_of_active_entries;
  if (numActiveEntries) {
    const sortOrder = getOrdersDefaultSortOrder(
      OrderTypes.ENTRY,
      isShort,
      isBreakout
    );
    activeEntryOrders = entryOrders
      .filter((orderData) => orderData.status === OrderStatus.PENDING)
      .sort((a, b) =>
        sortByNumberProperty(a, b, "edit_target_price", sortOrder)
      )
      .slice(0, numActiveEntries);
  }

  return {
    entryOrders,
    takeProfitOrders,
    stopOrders,
    ordersData,
    setEntryOrders,
    setTakeProfitOrders,
    setStopOrders,
    resetOrdersChanges,
    updateOrder,
    getOrdersByType,
    cancelOrder,
    moveOrderToCurrentPrice,
    moveStopOrderToBreakevenPrice,
    breakevenPrice,
    addOrder,
    updateOrdersWithAddedAmount,
    updateOrdersWithAddedQuantity,
    activeEntryOrders,
    setOrdersByType,
  };
};

export default useEditTradeOrders;
